/**
 * Pop-up Banner JavaScript
 * Handles popup display logic with time delay and click count triggers
 */

class PopupBanner {
  constructor() {
    this.popup = document.getElementById('popup-banner');
    this.overlay = this.popup?.querySelector('.popup-overlay');
    this.closeButton = this.popup?.querySelector('.popup-close');
    this.triggerType = this.popup?.dataset.triggerType || 'time';
    this.timeDelay = parseInt(this.popup?.dataset.timeDelay) || 5;
    this.clickCount = parseInt(this.popup?.dataset.clickCount) || 3;
    this.currentClicks = 0;
    this.hasShown = false;
    
    this.init();
  }

  init() {
    if (!this.popup) return;
    
    // Check if popup has already been shown in this session
    if (this.hasBeenShownInSession()) {
      console.log('🔵 Popup already shown in this session, skipping');
      return;
    }
    
    // Load click count from session storage
    this.loadClickCount();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Check if popup should show immediately
    this.checkInitialShow();
  }

  hasBeenShownInSession() {
    try {
      const sessionShown = sessionStorage.getItem('popup_shown_in_session');
      return sessionShown === 'true';
    } catch (e) {
      return false;
    }
  }

  markAsShownInSession() {
    try {
      sessionStorage.setItem('popup_shown_in_session', 'true');
      console.log('🔵 Marked popup as shown in session');
    } catch (e) {
      console.log('🔵 Error marking popup as shown:', e);
    }
  }

  loadClickCount() {
    try {
      const stored = localStorage.getItem('popup_click_count');
      this.currentClicks = stored ? parseInt(stored) : 0;
      console.log('🔵 Loaded click count from localStorage:', this.currentClicks);
    } catch (e) {
      this.currentClicks = 0;
      console.log('🔵 Error loading click count, using 0');
    }
  }

  saveClickCount() {
    try {
      localStorage.setItem('popup_click_count', this.currentClicks.toString());
      console.log('🔵 Saved click count to localStorage:', this.currentClicks);
    } catch (e) {
      console.log('🔵 Error saving click count:', e);
    }
  }

  setupEventListeners() {
    // Document click listener for click counting
    document.addEventListener('click', (e) => {
      this.handleClick(e);
    });

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
      });
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.hide();
      });
    }
    
    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup && this.popup.style.display === 'flex') {
        this.hide();
      }
    });
  }

  handleClick(e) {
    if (this.hasShown || this.triggerType !== 'clicks') return;

    this.currentClicks++;
    this.saveClickCount();
    
    console.log('🔵 Click detected! Count:', this.currentClicks, '/', this.clickCount);

    // Check if we've reached the click threshold
    if (this.currentClicks >= this.clickCount) {
      // Check if this click is on a link that will cause navigation
      const isLinkClick = e.target.closest('a') || e.target.tagName === 'A';
      
      if (isLinkClick) {
        console.log('🔵 Link click detected, deferring popup');
        // Defer showing popup until next page load
        localStorage.setItem('popup_show_on_load', 'true');
      } else {
        console.log('🔵 Non-link click, showing popup immediately');
        // Show immediately for non-link clicks
        this.show();
      }
    }
  }

  checkInitialShow() {
    if (this.hasShown) return;

    // Check if we should show on page load
    if (this.triggerType === 'clicks') {
      const shouldShow = localStorage.getItem('popup_show_on_load');
      if (shouldShow === 'true') {
        console.log('🔵 Showing popup from previous page load');
        localStorage.removeItem('popup_show_on_load');
        setTimeout(() => this.show(), 500);
        return;
      }
      
      // Show immediately if click count already reached
      if (this.currentClicks >= this.clickCount) {
        console.log('🔵 Click count already reached, showing popup');
        this.show();
        return;
      }
    }

    // Time delay trigger
    if (this.triggerType === 'time') {
      setTimeout(() => {
        if (!this.hasShown) {
          this.show();
        }
      }, this.timeDelay * 1000);
    }
  }

  show() {
    if (this.hasShown || !this.popup) return;
    
    this.hasShown = true;
    this.markAsShownInSession();
    this.resetClickCount(); // <-- Add this
    
    // Remove Tailwind hidden class and set display
    this.popup.classList.remove('tw-hidden');
    this.popup.style.display = 'flex';
    
    // Trigger animation
    setTimeout(() => {
      this.popup.classList.remove('tw-scale-95');
      this.popup.classList.add('tw-scale-100');
    }, 10);
  }

  hide() {
    if (!this.popup) return;
    
    this.popup.classList.remove('tw-scale-100');
    this.popup.classList.add('tw-scale-95');
    
    setTimeout(() => {
      this.popup.style.display = 'none';
      this.popup.classList.add('tw-hidden');
    }, 300);
  }

  resetClickCount() {
    this.currentClicks = 0;
    try {
      localStorage.setItem('popup_click_count', '0');
      console.log('🔵 Reset click count to 0');
    } catch (e) {
      console.log('🔵 Error resetting click count:', e);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new PopupBanner();
});

// Global close function
window.closePopup = function() {
  const popup = document.getElementById('popup-banner');
  if (popup) {
    popup.classList.add('tw-hidden');
    popup.style.display = 'none';
    popup.classList.remove('tw-scale-100');
    popup.classList.add('tw-scale-95');
  }
};

// Handle clicks on popup banner
window.handlePopupClick = function(event) {
  const popupContent = event.target.closest('.popup-content');
  const closeButton = event.target.closest('.popup-close');
  
  // If clicked on popup content or close button, don't close
  if (popupContent || closeButton) {
    return;
  }
  
  // If clicked outside popup content, close the popup
  closePopup();
}; 